import React, { useMemo } from 'react';
import { WarningIcon } from '../../../assets/icons/warning-icon.tsx';
import { RadioList, RadioListItem } from '../../../components/ui/button-radio-list.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { RouterOutputs } from '../../../utils/api.ts';
import { generateRecurrenceString, parseRecurrenceString } from '../../../utils/rrule.ts';
import { BaseConfirmationDialog } from '../../dialog/confirmation-dialog.tsx';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import type { ModalInnerProps } from '../../modal/index.tsx';
import { useDeleteEvent } from '../hooks/use-delete-event';
import { useUpdateEvent } from '../hooks/use-update-event';
export type DeleteEventModalProps = {
    onDeleted?: () => void;
    date: string;
    event: RouterOutputs['platoUser']['query']['events']['getEventById'];
} & ModalInnerProps;

export const DeleteEventModal = DialogStateManager.create<DeleteEventModalProps>(
    ({ onDeleted, event, date: occurenceDate }: DeleteEventModalProps) => {
        const { t } = useLocale();
        const { toast } = useToast();
        const modal = useDialog();
        const [deleteScope, setDeleteScope] = React.useState<'single' | 'series'>('single');

        const recurrenceOptions = useMemo(() => {
            if (!event.recurrence) {
                return undefined;
            }

            return parseRecurrenceString(event.recurrence, event.date);
        }, [event.recurrence, event.date]);

        const handleDeleteSuccess = () => {
            toast({
                icon: 'success',
                title: t('events__delete_success', { ns: 'events' }),
            });
            onDeleted?.();
            modal.remove();
        };

        const handleDeleteError = (message?: string) => {
            toast({
                icon: 'warning',
                title: t('events__delete_error', { ns: 'events' }),
                description: message,
            });
        };

        const { mutate: deleteEvent, isLoading: isDeleting } = useDeleteEvent(event, {
            onSuccess: handleDeleteSuccess,
            onError: handleDeleteError,
        });

        const { mutate: updateEvent, isLoading: isUpdating } = useUpdateEvent({
            onSuccess: handleDeleteSuccess,
            onError: handleDeleteError,
        });

        const handleDelete = () => {
            if (deleteScope === 'single' && recurrenceOptions) {
                // Adds exclusion to recurrence
                const { customer, event_id, agenda, agendaRich, ...data } = event;

                const excludedDates = new Set<string>(recurrenceOptions.excludedDates ?? []);

                excludedDates.add(occurenceDate);

                updateEvent({
                    eventId: event_id,
                    data: {
                        ...data,
                        title: data.title ?? '',
                        agenda: agendaRich,
                        customer_id: customer?.customerId ?? '',
                        recurrence: generateRecurrenceString(
                            {
                                ...recurrenceOptions,
                                excludedDates: Array.from(excludedDates),
                            },
                            occurenceDate,
                        ),
                    },
                });
            } else {
                deleteEvent();
            }
        };

        const handleCancel = () => {
            modal.remove();
        };

        return (
            <BaseConfirmationDialog
                focus="destroy"
                title={t('events__delete__title', { ns: 'events' })}
                description={t('events__delete_confirmation__body', {
                    ns: 'events',
                })}
                icon={WarningIcon}
            >
                {recurrenceOptions && (
                    <div className="self-start">
                        <RadioList
                            value={deleteScope}
                            onValueChange={(val) => {
                                setDeleteScope(val as 'single' | 'series');
                            }}
                        >
                            <RadioListItem
                                value="single"
                                label={t('events__delete__this_event', { ns: 'events' })}
                            />
                            <RadioListItem
                                value="series"
                                label={t('events__delete__entire_series', { ns: 'events' })}
                            />
                        </RadioList>
                    </div>
                )}
                <BaseConfirmationDialog.Actions>
                    <BaseConfirmationDialog.RejectButton onClick={handleCancel}>
                        {t('form__cancel')}
                    </BaseConfirmationDialog.RejectButton>
                    <BaseConfirmationDialog.AcceptButton
                        onClick={handleDelete}
                        isLoading={isDeleting || isUpdating}
                    >
                        {t('events__delete', { ns: 'events' })}
                    </BaseConfirmationDialog.AcceptButton>
                </BaseConfirmationDialog.Actions>
            </BaseConfirmationDialog>
        );
    },
);
